import {useState} from "react";
import {Card} from "../../../basic/card.tsx";
import {Button} from "../../../basic/button.tsx";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {CreateUserDialog} from "../../../dialogs/admin/CreateUserDialog";
import {EditUserDialog} from "../../../dialogs/admin/EditUserDialog";
import {PaginationControls} from "../../../shared/Pagination.tsx";
import {SearchInput} from "../../../shared/search/SearchInput.tsx";
import {UserTable} from "./UserTable.tsx";
import {UserActions} from "./UserActions.tsx";
import {useGetUsersQuery, VwpUser} from "../../../../store/generated/api.ts";

export function AdminUsersView() {
    const {t} = useTranslationWithKeys();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<VwpUser | null>(null);
    const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);

    const {
        data: usersData,
        isLoading,
        error,
        refetch
    } = useGetUsersQuery({
        page: currentPage,
        size: pageSize,
        ...(searchTerm && {search: searchTerm})
    });

    const users = usersData?.users || [];
    const pagination = usersData?.pagination;

    const handleUserClick = (user: VwpUser) => {
        setSelectedUser(user);
        setEditUserDialogOpen(true);
    };

    const handleCreateUser = () => {
        setCreateUserDialogOpen(true);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page - 1);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(0);
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(0);
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div>
                    <h1>{t('admin.users.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('admin.users.description')}</p>
                </div>
                <Card className="shadow-sm border-border/50">
                    <div className="p-6">
                        <div className="flex items-center justify-center h-64">
                            <p>{t('common.loading')}</p>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div>
                    <h1>{t('admin.users.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('admin.users.description')}</p>
                </div>
                <Card className="shadow-sm border-border/50">
                    <div className="p-6">
                        <div className="flex items-center justify-center h-64">
                            <div className="text-center">
                                <p className="text-red-500">{t('common.error')}</p>
                                <Button
                                    onClick={() => refetch()}
                                    className="mt-2"
                                >
                                    {t('common.retry')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1>{t('admin.users.title')}</h1>
                <p className="text-muted-foreground mt-1">{t('admin.users.description')}</p>
            </div>

            <Card className="shadow-sm border-border/50">
                <div className="p-6">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <SearchInput
                                placeholder={t('admin.users.searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                            <UserActions
                                totalUsers={pagination?.total || 0}
                                onCreateUser={handleCreateUser}
                            />
                        </div>
                        <UserTable
                            users={users}
                            onUserClick={handleUserClick}
                        />

                        {pagination && (
                            <PaginationControls
                                currentPage={pagination.page + 1}
                                totalPages={pagination.totalPages}
                                pageSize={pagination.size}
                                pageSizeOptions={[10, 25, 50, 100]}
                                paginationInfo={{
                                    start: pagination.page * pagination.size + 1,
                                    end: Math.min((pagination.page + 1) * pagination.size, pagination.total),
                                    total: pagination.total
                                }}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                            />
                        )}
                    </div>
                </div>
            </Card>

            <EditUserDialog
                open={editUserDialogOpen}
                onOpenChange={setEditUserDialogOpen}
                selectedUser={selectedUser}
            />

            <CreateUserDialog
                open={createUserDialogOpen}
                onOpenChange={setCreateUserDialogOpen}
            />
        </div>
    );
}